/**
 * weaponMods.ts — the Weapon Mod (attachment) system data model.
 *
 * A "mod" is a silly gamer-PC attachment looted from Starforge PC crates. Each weapon
 * can carry up to 6 mods; equipping them switches the weapon sprite to a pre-painted
 * combo image (e.g. {D,S,C} -> revolver_3_D_S_C.png) AND folds a stat buff into the gun.
 *
 * Art: per-weapon combo sets live in /weapons/mods/<weaponId>/, named
 *   <weaponId>_<count>_<codes-in-canonical-order>.png  (no mods = the weapon's base sprite).
 * Mod icons (for the bench/stash): /items/mods/mod_<name>.png
 *
 * NOTE: buff magnitudes below are PLACEHOLDERS pending Dylan's balance sign-off.
 */

export type ModCode = 'D' | 'S' | 'C' | 'P' | 'K' | 'M';

/** Canonical slot order — combo filenames list codes in exactly this order. */
export const MOD_ORDER: ModCode[] = ['D', 'S', 'C', 'P', 'K', 'M'];

/** Which engine stat a mod buffs (mapped to real WeaponInstance fields at the fold site). */
export type ModStat = 'damage' | 'attackSpeed' | 'crit' | 'pierce' | 'knockback' | 'multishot';

export interface WeaponMod {
  code: ModCode;
  id: string;          // stable id, also the icon stem
  name: string;        // shown in the bench
  blurb: string;       // what it looks like
  iconUrl: string;     // standalone mod icon for the stash/bench
  stat: ModStat;
  /** percent buff (for damage/attackSpeed/crit/knockback) */
  pct?: number;
  /** flat buff (for pierce/multishot) */
  flat?: number;
  noun: string;        // stat as a noun phrase, e.g. 'damage' / 'fire rate' (for rarity-scaled labels)
  label: string;       // base (= common) buff line
}

export const WEAPON_MODS: Record<ModCode, WeaponMod> = {
  D: { code: 'D', id: 'mod_damage',    name: 'Thingamajig',     blurb: 'Long reinforced barrel + muzzle brake', iconUrl: '/items/mods/mod_damage.png',    stat: 'damage',    pct: 25, noun: 'damage',      label: '+25% damage' },
  S: { code: 'S', id: 'mod_speed',     name: 'Doohickey',       blurb: 'PC fans + glowing coolant loops',       iconUrl: '/items/mods/mod_speed.png',     stat: 'attackSpeed', pct: 20, noun: 'fire rate',   label: '+20% fire rate' },
  C: { code: 'C', id: 'mod_crit',      name: 'Gizmo',           blurb: 'Lightning-charged targeting scope',     iconUrl: '/items/mods/mod_crit.png',      stat: 'crit',      pct: 15, noun: 'crit chance',  label: '+15% crit chance' },
  P: { code: 'P', id: 'mod_pierce',    name: 'Doodad',          blurb: 'Copper coil rails crackling energy',    iconUrl: '/items/mods/mod_pierce.png',    stat: 'pierce',    flat: 2, noun: 'pierce',      label: '+2 pierce' },
  K: { code: 'K', id: 'mod_knockback', name: 'Whatchamacallit', blurb: 'Plasma piston recoil booster',          iconUrl: '/items/mods/mod_knockback.png', stat: 'knockback', pct: 40, noun: 'knockback',   label: '+40% knockback' },
  M: { code: 'M', id: 'mod_multishot', name: 'Thingamabob',     blurb: 'Extra barrels bolted on',               iconUrl: '/items/mods/mod_multishot.png', stat: 'multishot', flat: 3, noun: 'projectiles', label: '+3 projectiles' },
};

// --- Rarity tiers: every mod comes in common/rare/legendary; higher rarity = bigger buff. ---
export type ModRarity = 'common' | 'rare' | 'legendary';
export const MOD_RARITIES: ModRarity[] = ['common', 'rare', 'legendary'];
export const MOD_RARITY_MULT: Record<ModRarity, number> = { common: 1, rare: 1.6, legendary: 2.4 };
export const MOD_RARITY_COLOR: Record<ModRarity, string> = { common: '#39c46b', rare: '#9a7bff', legendary: '#ffce4d' };
export const MOD_RARITY_NAME: Record<ModRarity, string> = { common: 'Common', rare: 'Rare', legendary: 'Legendary' };

/** The buff number for a mod at a given rarity (base value × rarity multiplier). */
export function modBuffValue(m: WeaponMod, r: ModRarity): number {
  const base = m.pct ?? m.flat ?? 0;
  return Math.round(base * MOD_RARITY_MULT[r]);
}
export function modBuffUnit(m: WeaponMod): string { return m.pct !== undefined ? '%' : ''; }
/** Human label for a mod at a rarity, e.g. "+40% damage". */
export function modLabel(m: WeaponMod, r: ModRarity): string {
  return `+${modBuffValue(m, r)}${modBuffUnit(m)} ${m.noun}`;
}

/** A weapon that has a painted mod-combo art set. */
export interface WeaponModSet {
  weaponId: string;
  dir: string;       // folder of combo images
  baseUrl: string;   // the no-mods sprite (the weapon's normal sprite)
  slots: number;     // max mods (≤6)
}

/** Registry of weapons whose mod art exists. Add weapons here as Manus delivers them. */
export const WEAPON_MOD_SETS: Record<string, WeaponModSet> = {
  // BETA 23.63 — dir bumped revolver → revolver2: Dylan's cowboy-revolver repaint
  // (63 combos re-painted on the SAA base art). New folder = new URLs = cache-bust;
  // same-URL pixel edits get swallowed by player caches.
  revolver: { weaponId: 'revolver', dir: '/weapons/mods/revolver2', baseUrl: '/weapons/weapon_03_revolver.png', slots: 6 },
};

/** True if this weapon supports mods (has an art set). */
export function weaponHasMods(weaponId: string): boolean {
  return !!WEAPON_MOD_SETS[weaponId];
}

/** Canonical-order the equipped codes (so lookups + filenames are deterministic). */
export function orderMods(equipped: Iterable<ModCode>): ModCode[] {
  const set = new Set(equipped);
  return MOD_ORDER.filter(c => set.has(c));
}

/**
 * Resolve the weapon sprite for a given equipped mod set.
 * No mods -> the base sprite. Otherwise the pre-painted combo image.
 */
export function modComboImage(weaponId: string, equipped: Iterable<ModCode>): string | null {
  const set = WEAPON_MOD_SETS[weaponId];
  if (!set) return null;
  const codes = orderMods(equipped);
  if (codes.length === 0) return set.baseUrl;
  return `${set.dir}/${weaponId}_${codes.length}_${codes.join('_')}.png`;
}
